import awsconfig from '../../aws-exports'
import { getAccessToken } from './../index'
import { requestV1 } from 'apis/base'
import {
  PostStatus,
  Poster,
  SamplePost,
  defaultSamplePostValue
} from 'apis/response_types'
const urlBase = awsconfig.yoor_app_api_url_base

export interface CreatePost {
  title: string
  content: string
  images: Array<string>
  videos: Array<{
    key: string
  }>
}

export interface LikeV1 {
  count: number
  is_liked: boolean
  users: Array<UserPublicV1>
}

export interface UserPublicV1 {
  id: string
  dynamo_id: string
  twitter_username: string
  twitter_screen_name: string
  door_screen_name: string
  description: string
  header_image_url: string
  icon_image_url: string
  header_image: string // Data URI(Base64)
  icon_image: string // Data URI(Base64)
  suspended: boolean
  suspended_at: string
}

export interface PostV1 {
  dynamo_id: string
  // title: string
  status: string
  content: string
  images: Array<{
    url_private: string
    url_public: string
    url_private_orig: string
  }>
  image_key: string | null
  videos: Array<{
    url: string
    filename: string
  }>
  comment_count: number
  created_at: string
  updated_at: string
  like: LikeV1
  uploaded: boolean
  owner: UserPublicV1
}

export interface PaginatedPostsV1 {
  posts: Array<PostV1>
  total_pages: number
  current_page: number
}

/**
 * 投稿一覧取得
 * @param doorID
 * @param params
 * @returns
 */
export const listDoorPostV1API = async (
  doorID: string,
  page?: number
): Promise<PaginatedPostsV1> => {
  const accessToken = await getAccessToken()
  const response = await fetch(
    `${urlBase}/api/doors/${doorID}/posts?page=${page || 1}`,
    {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Version': 'v1',
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
  return await response.json()
}

/**
 *  ドア投稿
 */
export const createDoorPostV1Api = async (doorID: string, data: CreatePost) => {
  const accessToken = await getAccessToken()
  const response = await fetch(`${urlBase}/api/doors/${doorID}/posts`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Version': 'v1',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify({ post: data })
  })
  return await response
}

export const createPostLikesV1API = async (postID: string) => {
  const accessToken = await getAccessToken()
  const response = await fetch(`${urlBase}/api/posts/${postID}/likes`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Version': 'v1',
      Authorization: `Bearer ${accessToken}`
    }
  })
  return await response.json()
}

export const deletePostLikesV1API = async (postID: string) => {
  const accessToken = await getAccessToken()
  const response = await fetch(`${urlBase}/api/posts/${postID}/likes`, {
    method: 'DELETE',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Version': 'v1',
      Authorization: `Bearer ${accessToken}`
    }
  })
  return await response
}

export const listPostLikesV1API = async (postID: string): Promise<LikeV1> => {
  const accessToken = await getAccessToken()
  const response = await fetch(`${urlBase}/api/posts/${postID}/likes`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Version': 'v1',
      Authorization: `Bearer ${accessToken}`
    }
  })
  return await response.json()
}

export const getDoorPostV1API = async (postID: string): Promise<PostV1> => {
  const accessToken = await getAccessToken()
  const response = await fetch(`${urlBase}/api/posts/${postID}`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Version': 'v1',
      Authorization: `Bearer ${accessToken}`
    }
  })
  return await response.json()
}

export const deleteDoorPostV1API = async (postID: string): Promise<any> => {
  const accessToken = await getAccessToken()
  const response = await fetch(`${urlBase}/api/posts/${postID}`, {
    method: 'delete',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Version': 'v1',
      Authorization: `Bearer ${accessToken}`
    }
  })
  return await response.json()
}

export const updateDoorPostV1Api = async (
  doorID: string,
  postID: string,
  data: CreatePost
) => {
  const accessToken = await getAccessToken()
  const response = await fetch(
    `${urlBase}/api/doors/${doorID}/posts/${postID}`,
    {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Version': 'v1',
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify({ post: data })
    }
  )
  return await response
}

export interface GetSamplePostResponse extends SamplePost {
  poster: Poster
  status: PostStatus
  door: {
    id: string
    title: string
    description: string
    is_owned: boolean
    is_staff: boolean
  }
  owner: {
    name: string
    icon: string
  }
  room: {
    id: number
    is_subscribing: boolean
  }
  other_sample_posts: Array<{
    id: string
    title: string
  }>
}

export const defaultSamplePostResponseValue: GetSamplePostResponse = {
  ...defaultSamplePostValue,
  poster: {
    door_screen_name: '',
    icon_image_url: '',
    twitter_screen_name: ''
  },
  status: 'published',
  door: {
    id: '',
    description: '',
    is_owned: true,
    title: '',
    is_staff: false
  },
  owner: {
    name: '',
    icon: ''
  },
  room: {
    id: 0,
    is_subscribing: true
  },
  other_sample_posts: []
}

export const getSamplePostV1 = async (doorID: string, samplePostID: string) => {
  let path = `/doors/${doorID}/sample_posts/${samplePostID}`
  return await requestV1<GetSamplePostResponse>('get', path)
}

export const getSamplePostPreviewV1 = async (
  doorID: string,
  postId: string,
  characterLimit: number | ''
) => {
  let query = ''
  if (characterLimit !== '') {
    query += `character_limit=${characterLimit}`
  }
  let path = `/doors/${doorID}/posts/${postId}/sample_preview?${query}`
  return await requestV1<GetSamplePostResponse>('get', path)
}
